import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';
import './LoginPage.css';
import { BeatLoader } from 'react-spinners';
import UserAuthContext from '../../context/UserAuthContext';
import { useNavigate } from 'react-router-dom';
import Turnstile from 'react-cloudflare-turnstile';
import ENDPOINTS from '../../endpoints';


const LoginPage: React.FC = () => {
  const { login } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: Enter email, 2: Verify OTP, 3: Change password
  const [resetLoading, setResetLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [token, setToken] = useState('');
  const [userData, setUserData] = useState<any>(null);
  const [directReferrals, setDirectReferrals] = useState<any>(null);
  const [tokenData, setTokenData] = useState<any>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log(token);
      const response = await axios.post(ENDPOINTS.login, {
        email,
        password,
        turnstileToken: token,
      });

      if (response.status === 200) {
        Toastify({
          text: response.data.message || 'Login successful',
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'center',
          stopOnFocus: true,
          style: {
            background: 'linear-gradient(to right, #00b09b, #96c93d)',
          },
        }).showToast();

        // setShowOtpModal(true);

        const { user_details, direct_referrals, token } = response.data; // Ensure the structure is correct
        console.log(user_details);
        console.log(direct_referrals);
        setUserData(user_details);
        setDirectReferrals(direct_referrals);
        setTokenData(token);


        login({ user_details, direct_referrals, token }); // Pass both user_details and token    

        if (user_details.isTwoFactorEnabled) {
          setShowOtpModal(true);
        } else {

        login({ user_details: userData, direct_referrals: directReferrals, token: tokenData });
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        Toastify({
          text: response.data.error || 'Login failed',
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'center',
          stopOnFocus: true,
          style: {
            background: 'linear-gradient(to right, #FF0000, #CB4335)',
          },
        }).showToast();
      }
    } catch (error: any) {
      let errorMessage = 'Login failed. Please try again.';
      console.log(error);
      // Check if error is an Axios error
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data.error || errorMessage;
      }
      Toastify({
        text: errorMessage,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: 'top',
        position: 'center',
        stopOnFocus: true,
        style: {
          background: 'linear-gradient(to right, #FF0000, #CB4335)',
        },
      }).showToast();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    setOtpError('');
    if (!otp) {
      setOtpError('OTP is required');
      return;
    }
    setVerifyLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.verify2FA, 
        { email, token: otp },
        {
          
        headers: {
          'Authorization': `Bearer ${tokenData}`,
          'Content-Type': 'application/json'
        }
      }
      );
      if (response.status === 200) {
        Toastify({
          text: "Verified successfully!",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();
        login({ user_details: userData, direct_referrals: directReferrals, token: tokenData });
        window.location.href = '/dashboard';
      } else {
        throw new Error(response.data.error || 'OTP verification failed');
      }
    } catch (error: any) {
      Toastify({
        text: error.response?.data?.error || "OTP verification failed",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setResetLoading(true);
      const response = await axios.post(ENDPOINTS.generateOTP, {
        email,
        type: showForgotPassword ? 'forgot-password' : 'login',
        resend: true,
      });
      if (response.status === 200) {
        Toastify({
          text: "OTP resent successfully!",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();
      }
    } catch (error: any) {
      Toastify({
        text: error.response?.data?.error || "Failed to resend OTP",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    } finally {
      setResetLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.generateOTP, {
        email: resetEmail,
        type: 'forgot-password',
      });
      if (response.status === 200) {
        Toastify({
          text: 'OTP sent successfully!',
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'center',
          stopOnFocus: true,
          style: {
            background: 'linear-gradient(to right, #00b09b, #96c93d)',
          },
        }).showToast();
        setResetStep(2);
      }
    } catch (error) {
      let errorMessage = 'Failed to send OTP. Please try again.';
      console.log(error);
      // Check if error is an Axios error
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data.error || errorMessage;
      }
      Toastify({
        text: errorMessage,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: 'top',
        position: 'center',
        stopOnFocus: true,
        style: {
          background: 'linear-gradient(to right, #FF0000, #CB4335)',
        },
      }).showToast();
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) {
      alert('Please enter the OTP');
      return;
    }
    setVerifyLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.verifyOTP, {
        email: resetEmail,
        otpCode: otp
      });
      if (response.status === 200) {
        Toastify({
          text: 'OTP verified successfully!',
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'center',
          stopOnFocus: true,
          style: {
            background: 'linear-gradient(to right, #00b09b, #96c93d)',
          },
        }).showToast();
        setResetStep(3);
      }
    } catch (error) {
      let errorMessage = 'OTP verification failed.. Please try again.';
      console.log(error);
      // Check if error is an Axios error
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data.error || errorMessage;
      }
      Toastify({
        text: errorMessage,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: 'top',
        position: 'center',
        stopOnFocus: true,
        style: {
          background: 'linear-gradient(to right, #FF0000, #CB4335)',
        },
      }).showToast();
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) {
      alert('Please enter a new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      Toastify({
        text: "Passwords do not match",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
      return;
    }
    setChangePasswordLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.forgotPassword, {
        email: resetEmail,
        new_password: newPassword,
      });
      if (response.status === 200) {
        Toastify({
          text: "Password changed successfully",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();
        setShowForgotPassword(false); // Go back to login form
        setResetStep(1); // Reset the steps
      }
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="web3-section">
        <h1>Login with Smart Rainmakers</h1>
        <img src={require('../../assets/images/login.png')} alt="Web3 Wallet" className="web3-image" />
        <p>Quick registration into your Rainmaker Account</p>
        <p>Manager your account with one single wallet address or email</p>
      </div>
      <div className="login-form-section">
        {!showForgotPassword ? (
          <>
            <h2>Smart Rainmakers Login</h2>
            <p>Welcome back! Log In with your Email.</p>
            <form onSubmit={handleLogin}>
              <div className="input-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  placeholder="Please enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <Turnstile
                turnstileSiteKey="0x4AAAAAABCjAMJ5eL6dWL_r"
                callback={setToken}
              />
              <button type="submit" className="login-button">
                {loading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Log In'}
              </button>
              <div className="login-options">
                <a href="#" className="forgot-password" onClick={() => setShowForgotPassword(true)}>Forgot password?</a>
                <a href="/register" className="register-link">Register for free</a>
              </div>
            </form>
          </>
        ) : (
          <>
            {resetStep === 1 && (
              <form onSubmit={handleForgotPassword}>
                <div className="input-group">
                  <label htmlFor="resetEmail">Email</label>
                  <input
                    type="email"
                    id="resetEmail"
                    placeholder="Please enter your email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="login-button" disabled={resetLoading}>
                  {resetLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Send Reset Email'}
                </button>
              </form>
            )}
            {resetStep === 2 && (
              <form onSubmit={handleVerifyOtp}>
                <div className="input-group">
                  <label htmlFor="otp">Enter OTP</label>
                  <input
                    type="text"
                    id="otp"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="login-button" disabled={verifyLoading}>
                  {verifyLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Verify OTP'}
                </button>
                <button onClick={handleForgotPassword} className="resend-login-button" disabled={resetLoading}>
                  {resetLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Resend OTP'}
                </button>
              </form>
            )}
            {resetStep === 3 && (
              <form onSubmit={handleChangePassword}>
                <div className="input-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="login-button" disabled={changePasswordLoading}>
                  {changePasswordLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Change Password'}
                </button>
              </form>
            )}
          </>
        )}
      </div>

      {showOtpModal && (
        <div className="otp-modal">
          <div className="otp-modal-content">
            <h3>Verify 2FA</h3>
            <input
              type="text"
              placeholder="Enter Code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            {otpError && <span className="error-message">{otpError}</span>}
            <button
              className="verify-button" disabled={verifyLoading}
              onClick={handleOtpVerification}
            >
              Verify
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
